"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnUtils = void 0;
exports.orcadDesignId = orcadDesignId;
exports.isHierarchicalBlockPlacement = isHierarchicalBlockPlacement;
exports.unresolvedBlockIsPhysicalPart = unresolvedBlockIsPhysicalPart;
exports.placedViewSuffixOf = placedViewSuffixOf;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
function orcadDesignId(canonicalName) {
    let h = 0x811c9dc5;
    for (let i = 0; i < canonicalName.length; i++) {
        h ^= canonicalName.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return `d_${(h >>> 0).toString(16).padStart(8, '0')}`;
}
function isHierarchicalBlockPlacement(partInst) {
    return partInst.ContentsViewType === 1;
}
function unresolvedBlockIsPhysicalPart(expandsIntoSubSheet, design, partInst) {
    if (!isHierarchicalBlockPlacement(partInst) || expandsIntoSubSheet) {
        return false;
    }
    const footprint = partInst.UserProperty?.find((property) => property.Name === 'PCB Footprint')?.Value;
    if (!footprint || footprint.trim().length === 0) {
        return false;
    }
    return placementHasCachedPins(design, partInst);
}
function placedViewSuffixOf(graphicName) {
    const match = /(\.(?:Normal|Convert|IEEE))$/i.exec(graphicName);
    return match === null ? undefined : match[1];
}
function placementHasCachedPins(design, partInst) {
    const packageResolution = OrcadDsnUtils.findBestPackageForPartInst(design.Cache, partInst);
    if (!packageResolution) {
        return false;
    }
    const placedViewSuffix = placedViewSuffixOf(partInst.GraphicName);
    let anyViewHasPins = false;
    for (const libPart of packageResolution.pkg.LibPart) {
        if (!libPart.CellName || !OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
            continue;
        }
        for (const view of [...(libPart.NormalView ?? []), ...(libPart.ConvertView ?? [])]) {
            const hasPins = (view.PinScalar?.length ?? 0) > 0 || (view.PinBus?.length ?? 0) > 0;
            if (view.Suffix === placedViewSuffix) {
                return hasPins;
            }
            if (hasPins) {
                anyViewHasPins = true;
            }
        }
    }
    return placedViewSuffix === undefined && anyViewHasPins;
}
class OrcadDsnUtils {
    static findPackageByIdentity(cache, sourceLibName, packageName, packageIndex) {
        const packages = cache?.Package;
        if (!packages) {
            return undefined;
        }
        if (packageIndex !== undefined) {
            const pkg = packages[packageIndex];
            if (pkg?.SourceLibName === sourceLibName && pkg.Name === packageName) {
                return pkg;
            }
        }
        return this.findLastPackageByIdentity(cache, sourceLibName, packageName);
    }
    static findLastPackageByIdentity(cache, sourceLibName, packageName) {
        const packages = cache?.Package;
        if (!packages) {
            return undefined;
        }
        for (let i = packages.length - 1; i >= 0; i--) {
            const pkg = packages[i];
            if (pkg.SourceLibName === sourceLibName && pkg.Name === packageName) {
                return pkg;
            }
        }
        return undefined;
    }
    static findBestPackageForPartInst(cache, partInst) {
        const packages = cache?.Package;
        if (!packages) {
            return undefined;
        }
        let lastMatchingPackage;
        let bestCompatiblePackage;
        for (let packageIndex = 0; packageIndex < packages.length; packageIndex++) {
            const pkg = packages[packageIndex];
            if (pkg.SourceLibName !== partInst.LibName || pkg.Name !== partInst.PkgName) {
                continue;
            }
            lastMatchingPackage = { pkg, packageIndex };
            const score = this.scorePackageCompatibility(pkg, partInst);
            if (score === undefined) {
                continue;
            }
            if (!bestCompatiblePackage || score > bestCompatiblePackage.score) {
                bestCompatiblePackage = { pkg, packageIndex, score };
            }
        }
        return bestCompatiblePackage ?? lastMatchingPackage;
    }
    static scorePackageCompatibility(pkg, partInst) {
        let bestScore;
        for (const libPart of pkg.LibPart) {
            if (!libPart.CellName || !OrcadDsnUtils.graphicNameMatchesCellName(partInst.GraphicName, libPart.CellName)) {
                continue;
            }
            for (const normalView of libPart.NormalView ?? []) {
                const score = this.scoreNormalViewCompatibility(normalView, partInst);
                if (score === undefined) {
                    continue;
                }
                if (bestScore === undefined || score > bestScore) {
                    bestScore = score;
                }
            }
        }
        return bestScore;
    }
    static scoreNormalViewCompatibility(normalView, partInst) {
        const ports = [...(partInst.PortInstScalar ?? []), ...(partInst.PortInstBus ?? [])];
        if (ports.length === 0) {
            return 0;
        }
        const pinsByPosition = new Map();
        for (const pin of [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])]) {
            pinsByPosition.set(pin.Position, pin);
        }
        let score = 0;
        for (const port of ports) {
            const pin = pinsByPosition.get(port.Position);
            if (!pin) {
                return undefined;
            }
            if (pin.Name !== port.Name) {
                return undefined;
            }
            if (pin.HotptX !== port.HotptX ||
                pin.HotptY !== port.HotptY ||
                pin.StartX !== port.StartX ||
                pin.StartY !== port.StartY) {
                return undefined;
            }
            score += 100;
            if (pin.Type === port.Type) {
                score += 10;
            }
            if (Boolean(pin.Visible) === port.Visible) {
                score += 1;
            }
        }
        return score;
    }
    static getRefSchematicPrefix(relativeTo, targetFile, schematic) {
        const path = OrcadDsnUtils.resolveFilePath(relativeTo, targetFile);
        return `${path}::${schematic}`;
    }
    static resolveFilePath(relativeToFile, targetFile) {
        const normalizedTarget = targetFile?.replace(/\\/g, '/');
        return sigma_engine_shared_1.Files.resolvePath(sigma_engine_shared_1.Files.dirnamePath(relativeToFile.replace(/\\/g, '/').replace(/^\.\\/, '')), normalizedTarget ?? sigma_engine_shared_1.Files.basenamePath(relativeToFile));
    }
    static getRefViewName(relativeTo, targetFile, schematic, page) {
        return `${OrcadDsnUtils.getRefSchematicPrefix(relativeTo, targetFile, schematic)}::${page}`;
    }
    static isRefForDesignSchematic(ref, sourceFile, schematic) {
        return ref.startsWith(`${sourceFile}::${schematic}::`);
    }
    static getTitleBlockUserProp(tb, name) {
        return (0, sigma_engine_shared_1.toArray)(tb)
            .map((t) => t.UserProperty?.find((p) => p.Name === name))
            .find((v) => v !== undefined);
    }
    static graphicNameMatchesCellName(graphicName, cellName) {
        if (!graphicName.startsWith(cellName)) {
            return false;
        }
        return graphicName.length === cellName.length || graphicName[cellName.length] === '.';
    }
    static orcadRotationToCadstrom(rotation, mirror) {
        return {
            angle: rotation * 90,
            mirrored: mirror === 1 ? [rotation === 1 || rotation === 3, rotation === 2 || rotation === 0] : [false, false],
        };
    }
    static computeModuleCenter(inst, boundingBox) {
        const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
        let center = pointTransform([inst.LocX, inst.LocY]);
        if (inst.Rotation % 2 === 1) {
            center = oneWeirdTrick(center);
        }
        return center;
    }
    static transformPinToSchematicCoords(symbolPin, partInst, boundingBox) {
        const bboxCenter = [(boundingBox.X1 + boundingBox.X2) / 2, (boundingBox.Y1 + boundingBox.Y2) / 2];
        let pin = [symbolPin.HotptX - bboxCenter[0], symbolPin.HotptY - bboxCenter[1]];
        const rot = partInst.Rotation % 4;
        if (rot === 1) {
            pin = [pin[1], -pin[0]];
        }
        else if (rot === 2) {
            pin = [-pin[0], -pin[1]];
        }
        else if (rot === 3) {
            pin = [-pin[1], pin[0]];
        }
        const { mirrored } = OrcadDsnUtils.orcadRotationToCadstrom(partInst.Rotation, partInst.Mirror);
        if (mirrored[1]) {
            pin = [-pin[0], pin[1]];
        }
        if (mirrored[0]) {
            pin = [pin[0], -pin[1]];
        }
        const moduleCenter = OrcadDsnUtils.computeModuleCenter(partInst, boundingBox);
        return [moduleCenter[0] + pin[0], moduleCenter[1] + pin[1]];
    }
}
exports.OrcadDsnUtils = OrcadDsnUtils;
//# sourceMappingURL=OrcadDsnUtils.js.map